import { visualRoutes } from "@/Router/routes";
import NotificationsList from "@/components/Notifications/NotificationsList";
import VenuePortalUserOptionsAutocomplete from "@/components/VenuePortalUserOptionsAutocomplete/VenuePortalUserOptionsAutocomplete";
import useLocalization from "@/hooks/useLocalization";
import useUser from "@/hooks/useUser";
import icons from "@/tools/icons";
import {
  Badge,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  useTheme,
} from "@mui/material";
import { Fragment, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoWithEnvironment from "../../LogoWithEnvironment";

export default function SideBar() {
  const navigate = useNavigate();
  const {
    logout,
    isMtUser,
    notificationLinks,
    numUnreadNotifications,
    notificationsLoading,
  } = useUser();
  const { t } = useLocalization();

  const theme = useTheme();
  const iconSx = { color: theme.custom.sideBarTextColor };

  const currentPath = useLocation().pathname;
  const [notificationsAnchor, setNotificationsAnchor] =
    useState<HTMLElement | null>(null);
  const notificationsOpen = Boolean(notificationsAnchor);

  return (
    <Box sx={{ position: "sticky", top: 0, left: 0, minWidth: "200px" }}>
      <List
        disablePadding
        sx={{
          backgroundColor: "primary.dark",
          height: "100vh",
          color: theme.custom.sideBarTextColor,
        }}
      >
        <ListItem sx={{ cursor: "pointer" }} onClick={() => navigate("/")}>
          <LogoWithEnvironment />
        </ListItem>

        {visualRoutes.filter((route) => !route.requiresMtUser || isMtUser).map(({ path, visual: { id, icon, ariaLabel } }) => (
          <Fragment key={id}>
            <ListItem disablePadding>
              <ListItemButton
                selected={currentPath === path}
                onClick={() => {
                  navigate(path);
                }}
              >
                <ListItemIcon sx={iconSx}>
                  <span aria-label={ariaLabel}>{icon}</span>
                </ListItemIcon>

                <ListItemText
                  sx={{ textWrap: "nowrap" }}
                  primary={t(`page:${id}`)}
                />
              </ListItemButton>
            </ListItem>
            {id === "home" && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={(event) =>
                    setNotificationsAnchor(event.currentTarget)
                  }
                  selected={notificationsOpen}
                >
                  <ListItemIcon sx={iconSx}>
                    <Badge
                      color="secondary"
                      badgeContent={numUnreadNotifications}
                    >
                      <icons.Notifications />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    sx={{ textWrap: "nowrap" }}
                    primary={t("page:notifications")}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </Fragment>
        ))}

        <Box sx={{ position: "absolute", width: "100%", bottom: 0 }}>
          {isMtUser && (
            <ListItem disablePadding>
              <ListItemButton>
                <ListItemIcon sx={iconSx}>
                  <icons.Fingerprint />
                </ListItemIcon>
                <VenuePortalUserOptionsAutocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  forcePopupIcon={false}
                  sx={{ maxWidth: "100px" }}
                  slotProps={{
                    popper: {
                      sx: { width: "auto !important" },
                    },
                  }}
                  textFieldProps={{
                    variant: "standard",
                    sx: {
                      "& .MuiInputBase-input": {
                        WebkitTextFillColor: theme.custom.sideBarTextColor,
                        color: theme.custom.sideBarTextColor,
                      },
                      "& .MuiInputBase-root:before": {
                        borderBottomColor: theme.custom.sideBarTextColor,
                      },
                      "& .MuiInputBase-root:hover:not(.Mui-disabled):before": {
                        borderBottomColor: theme.custom.sideBarTextColor,
                      },
                      "& .MuiInputBase-root:after": {
                        borderBottomColor: theme.custom.sideBarTextColor,
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon sx={iconSx}>
                <span aria-label="logout icon">
                  <icons.Logout />
                </span>
              </ListItemIcon>
              <ListItemText primary={t("command:logOut")} />
            </ListItemButton>
          </ListItem>
        </Box>
      </List>
      <Popover
        open={notificationsOpen}
        anchorEl={notificationsAnchor}
        onClose={() => setNotificationsAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { padding: 2, width: 480, maxHeight: 480, overflowY: "auto" },
          },
        }}
      >
        <NotificationsList
          notificationLinks={notificationLinks}
          loading={notificationsLoading}
        />
      </Popover>
    </Box>
  );
}
